import React from 'react';

import { graphql, Link } from 'gatsby';

import { Button, styled } from '@mui/material';

import { Language } from '@mewa/types';
import { Awards, cssVars } from '@mewa/ui';

import { slugifyByLang } from '../../../../libs/slugify';
import { StrapiImage } from '../../../templates/dynamic-content-page/content-components/image';
import { AwardData } from '../award';
import { StrapiComponentType } from '../types';
import { MewaEssentialsSection } from './types';

export const AwardsFragment = graphql`
  fragment AwardsFragment on STRAPI__COMPONENT_MEWA_ESSENTIALS_AWARDS {
    tabTitle
    awards {
      ...AwardFragment
    }
    linkLabel
    linkTopic {
      topic: Topic
    }
    strapi_component
  }
`;

export const componentName = 'mewa-essentials.awards';

export type AwardsData = StrapiComponentType<typeof componentName> & {
  headline: string;
  awards: AwardData[] | null;
  linkTopic: { topic: string };
  linkLabel: string;
};

export const isAwards = (
  component: MewaEssentialsSection,
): component is MewaEssentialsSection<AwardsData> => {
  return component.strapi_component === componentName;
};

export const StrapiAwards = ({
  data: { awards = [], linkTopic, linkLabel },
  lang,
  pressOverviewSlug,
}: {
  data: AwardsData;
  lang: Language;
  pressOverviewSlug: string;
}) => {
  const awardsData = (awards ?? []).map((award) => ({
    ...award,
    image: (
      <StrapiImage alt={award.title} image={award.image} objectFit="contain" />
    ),
  }));

  return (
    <>
      <Awards
        awards={awardsData}
        slidesPerView={{
          1680: { slidesPerView: 3 },
          [cssVars.maxWidthVp3]: { slidesPerView: 2 },
          1280: { slidesPerView: 1 },
          [cssVars.maxWidthVp2]: { slidesPerView: 2 },
        }}
      ></Awards>
      {linkTopic?.topic && linkLabel && (
        <LinkContainer>
          <Button
            component={Link}
            to={`${pressOverviewSlug}#${slugifyByLang(linkTopic.topic, lang)}`}
          >
            {linkLabel}
          </Button>
        </LinkContainer>
      )}
    </>
  );
};

const LinkContainer = styled('div')(({ theme }) => ({
  margin: '26px auto -8px',
  width: 'max-content',

  [theme.breakpoints.up('sm')]: {
    margin: '30px auto 0',
  },

  [theme.breakpoints.up('md')]: {
    margin: '40px auto 0',
  },

  [theme.breakpoints.up('lg')]: {
    margin: '50px auto 0',
  },
}));
